import { FC, useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { AutocompleteRenderInputParams, Box, Svg, SvgBubble, Tooltip } from '@hopskipdrive/react-components';

import { Divider, FormInputText, InputLabel, MarginLeft, PhoneNumberInput, Select } from './styles';
import { contactSchema } from './validationSchema';

interface IContactForm {
  onSubmit: (data) => void;
  onCancel: () => void;
  contact?: RidersContactForm;
}

export const ContactForm: FC<IContactForm> = ({ contact, onSubmit, onCancel }) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<RidersContactForm>({
    defaultValues: useMemo(
      () => ({
        firstName: contact?.firstName || '',
        lastName: contact?.lastName || '',
        role: contact?.role || 'caregiver',
        relationship: contact?.relationship || '',
        phoneNumber: contact?.phoneNumber || '',
        email: contact?.email || '',
      }),
      [contact]
    ),
    resolver: yupResolver(contactSchema()),
  });

  const watchRole: RidersContactForm['role'] = useWatch({
    control,
    name: 'role',
    defaultValue: contact?.role || 'caregiver',
  });

  return (
    <form
      id="contact"
      aria-label="form"
      onSubmit={handleSubmit((data: RidersContactForm) => (isDirty ? onSubmit(data) : onCancel()))}
    >
      <FirstName control={control} errors={errors} />
      <LastName control={control} errors={errors} />
      <Divider />
      <Role control={control} errors={errors} />
      <Relationship control={control} />
      <Divider />
      <PhoneNumber errors={errors} control={control} />
      {watchRole === 'caregiver' && <Email control={control} errors={errors} />}
    </form>
  );
};

const FirstName = ({ errors, control }) => (
  <Box display="flex" alignItems="center" mb={4}>
    <InputLabel fieldtype="required" htmlFor="firstName">
      First name
    </InputLabel>
    <Controller
      name="firstName"
      control={control}
      render={({ field: { value, onChange } }) => (
        <FormInputText
          autoFocus
          id="firstName"
          placeholder=""
          data-testid="firstName"
          variant="outlined"
          value={value}
          error={!!errors.firstName}
          helperText={errors.firstName ? errors.firstName.message : ''}
          onChange={onChange}
        />
      )}
    />
  </Box>
);

const LastName = ({ errors, control }) => (
  <Box display="flex" alignItems="center">
    <InputLabel fieldtype="required" htmlFor="lastName">
      Last name
    </InputLabel>
    <Controller
      name="lastName"
      control={control}
      render={({ field: { value, onChange } }) => (
        <FormInputText
          id="lastName"
          placeholder=""
          data-testid="lastName"
          variant="outlined"
          value={value}
          error={!!errors.lastName}
          helperText={errors.lastName ? errors.lastName.message : ''}
          onChange={onChange}
        />
      )}
    />
  </Box>
);

const Role = ({ control, errors }) => (
  <Box display="flex" alignItems="center" mb={4}>
    <InputLabel fieldtype="required" htmlFor="role">
      Role
    </InputLabel>
    <Controller
      control={control}
      name="role"
      render={({ field: { value, onChange } }) => (
        <Select
          options={[
            { label: 'Caregiver', value: 'caregiver' },
            { label: 'Emergency Contact', value: 'emergency_contact' },
          ]}
          value={value}
          setValue={onChange}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <FormInputText
              {...params}
              variant="outlined"
              fullWidth
              error={!!errors.role}
              helperText={errors.role ? errors.role.message : ''}
              data-testid="contactRole"
            />
          )}
        />
      )}
    />
    <MarginLeft>
      <Tooltip
        title="Caregivers have permission to monitor rides, be contacted by CareDrivers, and cancel rides.
          Emergency Contacts are contacted by SafeRide Support only in emergency events where Caregivers cannot be reached."
      >
        <SvgBubble>
          <Svg icon="question" />
        </SvgBubble>
      </Tooltip>
    </MarginLeft>
  </Box>
);

const Relationship = ({ control }) => (
  <Box display="flex" alignItems="center">
    <InputLabel htmlFor="relationship">Relationship</InputLabel>
    <Controller
      control={control}
      name="relationship"
      render={({ field: { value, onChange } }) => (
        <Select
          options={[
            { label: 'Parent or Guardian', value: 'guardian' },
            { label: 'Relative', value: 'relative' },
            { label: 'Social Worker', value: 'social_worker' },
            { label: 'Educational Specialist', value: 'ed_specialist' },
            { label: 'Other', value: 'other' },
          ]}
          value={value}
          setValue={onChange}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <FormInputText {...params} variant="outlined" data-testid="relationship" fullWidth />
          )}
        />
      )}
    />
  </Box>
);

const PhoneNumber = ({ errors, control }) => (
  <Box display="flex" alignItems="center" mb={4}>
    <InputLabel fieldtype="required" htmlFor="phoneNumber">
      Mobile phone
    </InputLabel>

    <Controller
      control={control}
      name="phoneNumber"
      render={({ field: { onChange, value, name } }) => (
        <PhoneNumberInput
          variant="outlined"
          name={name}
          id={name}
          data-testid="phoneNumber"
          onValueChange={(value) => onChange(value)}
          initialValue={value}
          error={!!errors.phoneNumber}
          helperText={errors.phoneNumber ? errors.phoneNumber.message : ''}
        />
      )}
    />
  </Box>
);

const Email = ({ errors, control }) => (
  <Box display="flex" alignItems="center">
    <InputLabel htmlFor="email">Email</InputLabel>
    <Controller
      name="email"
      control={control}
      render={({ field: { onChange, value } }) => (
        <FormInputText
          id="email"
          placeholder=""
          data-testid="email"
          variant="outlined"
          value={value}
          error={!!errors.email}
          helperText={errors.email ? errors.email.message : ''}
          onChange={onChange}
        />
      )}
    />
  </Box>
);
